import { Link } from "react-router-dom";
import { ArrowRight, Wrench, ShieldCheck, Ship } from "lucide-react";
import TransparentImage from "@/components/TransparentImage";

const STARTER_IMG = "https://customer-assets.emergentagent.com/job_motor-ignite/artifacts/nrq4iof6_image.png";
const ALTERNATOR_IMG = "https://customer-assets.emergentagent.com/job_motor-ignite/artifacts/qfvhu65w_image.png";

export default function About() {
  return (
    <main data-testid="about-page" className="min-h-[80vh]">
      {/* HERO */}
      <section className="border-b border-ink-600 bg-ink-800">
        <div className="max-w-[1400px] mx-auto grid grid-cols-12 gap-0">
          <div className="col-span-12 md:col-span-8 px-5 md:px-12 py-16 md:py-24 border-r border-ink-600">
            <div className="font-mono-tech text-[10px] text-copper mb-4">/ ABOUT</div>
            <h1
              data-testid="about-heading"
              className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.95] mb-8"
            >
              Get to <span className="chrome-text">know us.</span>
            </h1>
            <p
              data-testid="about-experience"
              className="text-steel-200 text-base md:text-lg leading-relaxed max-w-2xl space-y-4"
            >
              With over 45 years of experience in the automotive industry,
              Washington Rotating Electrics specialises in starter motors and
              alternators. Built on decades of knowledge and expertise, we are
              committed to providing reliable products, quality workmanship, and
              exceptional customer service. Whether you&apos;re a private vehicle
              owner, garage, or commercial operator, you can trust us to deliver
              dependable solutions that keep your vehicles running smoothly.
            </p>
          </div>
          <div className="hidden md:flex col-span-4 relative overflow-hidden border-l border-ink-600 min-h-[420px] flex-col">
            <div className="flex-1 relative flex items-center justify-center p-6 border-b border-ink-600">
              <TransparentImage
                src={STARTER_IMG}
                alt="Starter motor"
                className="max-w-full max-h-[200px] object-contain drop-shadow-[0_10px_20px_rgba(0,0,0,0.6)]"
              />
              <div className="absolute bottom-3 left-4 font-mono-tech text-[9px] text-copper">
                ⏵ STARTER MOTORS
              </div>
            </div>
            <div className="flex-1 relative flex items-center justify-center p-6">
              <TransparentImage
                src={ALTERNATOR_IMG}
                alt="Alternator"
                className="max-w-full max-h-[200px] object-contain drop-shadow-[0_10px_20px_rgba(0,0,0,0.6)]"
              />
              <div className="absolute bottom-3 left-4 font-mono-tech text-[9px] text-copper">
                ⏵ ALTERNATORS
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* BODY */}
      <section className="border-b border-ink-600">
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 md:grid-cols-3">
          {[
            {
              Icon: Wrench,
              t: "Four & a half decades on the bench",
              b: "Hands-on experience across HGV, PSV, industrial machinery and marine engines. If it cranks or charges, we know it inside out.",
            },
            {
              Icon: ShieldCheck,
              t: "Specialists, not generalists",
              b: "Starter motors and alternators are our trade — that's all we do, day in and day out, and that's why we get it right first time.",
            },
            {
              Icon: Ship,
              t: "North East England based",
              b: "Serving HGV operators, PSV depots, industrial sites and marine yards across the UK with next-day delivery and a phone that gets answered.",
            },
          ].map(({ Icon, t, b }, i) => (
            <div
              key={t}
              className={`p-8 md:p-10 border-ink-600 ${i < 2 ? "md:border-r" : ""} border-b md:border-b-0`}
            >
              <Icon size={28} strokeWidth={1.6} className="text-copper mb-5" />
              <h3 className="font-display text-2xl mb-3">{t}</h3>
              <p className="text-steel-400 text-sm leading-relaxed">{b}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="border-b border-ink-600 bg-ink-800">
        <div className="max-w-[1400px] mx-auto px-5 md:px-12 py-16 md:py-20 flex flex-col md:flex-row justify-between md:items-end gap-6">
          <div>
            <div className="font-mono-tech text-[10px] text-copper mb-3">/ READY WHEN YOU ARE</div>
            <h2 className="font-display text-3xl md:text-5xl leading-[0.95] max-w-2xl">
              Tap into 45 years of know-how.
            </h2>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/quote" data-testid="about-quote-cta" className="btn-copper">
              Request a Quote <ArrowRight size={14} />
            </Link>
            <Link to="/contact" className="btn-ghost">
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
